import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fetchSavedModels } from '../../Utils/httpCommandBridge';
import {
  getActionAIModelField,
  getActionAIModelTypes,
} from '../../Utils/actionAIModelUtils';
import {
  useActionAIModels,
  useActionType,
} from './ActionScopeContext';

const MODEL_LABEL_KEYS = {
  text: ['global_setting.text_model', 'Text model for this action'],
  vision: ['global_setting.vision_model', 'Vision model for this action'],
  audio: ['global_setting.audio_model', 'Audio model for this action'],
};

const formatModelLabel = (model) => {
  const provider = String(model?.provider || '').trim();
  const modelName = String(model?.model_name || '').trim();
  const parts = [provider, modelName].filter(Boolean);
  const baseLabel = parts.length > 0 ? parts.join(' • ') : 'Configured model';
  const normalizedLabel = baseLabel.replace(String.fromCharCode(0x00e2, 0x20ac, 0x00a2), ' / ');
  return model?.id === undefined || model?.id === null
    ? normalizedLabel
    : `${normalizedLabel} (#${model.id})`;
};

const ActionAIModelSettings = ({ asGridItem = true }) => {
  const { t } = useTranslation();
  const actionType = useActionType();
  const { actionAIModels, setActionAIModel, isPluginAction } = useActionAIModels();
  const modelTypes = useMemo(
    () => getActionAIModelTypes(actionType, isPluginAction),
    [actionType, isPluginAction]
  );
  const [savedModels, setSavedModels] = useState({ text: [], vision: [], audio: [] });

  useEffect(() => {
    if (modelTypes.length === 0) {
      return undefined;
    }

    let isMounted = true;
    fetchSavedModels()
      .then((payload) => {
        if (!isMounted) {
          return;
        }
        const models = payload?.saved_models || payload || {};
        setSavedModels({
          text: Array.isArray(models.text) ? models.text : [],
          vision: Array.isArray(models.vision) ? models.vision : [],
          audio: Array.isArray(models.audio) ? models.audio : [],
        });
      })
      .catch((error) => {
        console.error('Failed to load saved AI models for action settings:', error);
      });

    return () => {
      isMounted = false;
    };
  }, [modelTypes]);

  if (modelTypes.length === 0) {
    return null;
  }

  const settingsContent = (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        border: '1px solid #e7e7f2',
        borderRadius: 2,
        backgroundColor: '#fafaff',
      }}
    >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          {t('global_setting.model_settings_section', 'AI model for this action')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            'global_setting.model_selection_info',
            'Leave a field on the default option to use the project or global model assignment.'
          )}
        </Typography>
        <Grid container spacing={2}>
          {modelTypes.map((modelType) => {
            const fieldName = getActionAIModelField(modelType);
            const [labelKey, fallbackLabel] = MODEL_LABEL_KEYS[modelType] || [
              '',
              `${modelType} model for this action`,
            ];
            const models = savedModels[modelType] || [];

            return (
              <Grid item xs={12} md={modelTypes.length > 1 ? 6 : 12} key={modelType}>
                <TextField
                  select
                  fullWidth
                  data-testid={`action-ai-model-select-${modelType}`}
                  label={labelKey ? t(labelKey, fallbackLabel) : fallbackLabel}
                  value={actionAIModels[fieldName] || ''}
                  onChange={(event) => setActionAIModel(modelType, event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected) {
                        return null;
                      }
                      const model = models.find((candidate) => String(candidate.id) === String(selected));
                      return model ? formatModelLabel(model) : String(selected);
                    }
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }
                  }}
                  helperText={t(
                    'global_setting.no_model_configured',
                    'Default: use the project or global assignment'
                  )}
                >
                  <MenuItem value="">
                    {t('global_setting.project_model_reset', 'Use project/global assignment')}
                  </MenuItem>
                  {models.length > 0 ? (
                    models.map((model) => (
                      <MenuItem key={`${modelType}-${model.id}`} value={String(model.id)}>
                        {formatModelLabel(model)}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="__no_models__">
                      {t('global_setting.no_models_available', 'No models configured yet.')}
                    </MenuItem>
                  )}
                </TextField>
              </Grid>
            );
          })}
        </Grid>
    </Box>
  );

  return asGridItem ? <Grid item xs={12}>{settingsContent}</Grid> : settingsContent;
};

export default ActionAIModelSettings;
